class SearchTyped extends HTMLElement {
  constructor() {
    super();
    this._listeners = [];
    this._drawerObserver = null;
    this._cycleTimer = null;
    this._openTimer = null;
    this.isDismissed = false;
    this.initialized = false;
  }

  connectedCallback() {
    const input = this.input;
    if (!input) return;

    const typed = this.closest('.typed');
    if (typed) {
      this.on(typed, 'pointerdown', this.onTypedPointerDown.bind(this));
    }

    this.on(input, 'pointerdown', this.onPointerDown.bind(this));
    this.on(input, 'blur', this.onBlur.bind(this));
    this.on(input, 'input', this.onInput.bind(this));
    this.on(input, 'focus', this.onFocus.bind(this));

    const form = this.closest('form');
    if (form) this.on(form, 'reset', this.onReset.bind(this));

    if (input.value.trim()) {
      this.dismissTyped();
      return;
    }

    if (this.closest('search-drawer')) {
      this.bindDrawer();
    } else {
      this.scheduleInit();
    }
  }

  disconnectedCallback() {
    this._drawerObserver?.disconnect();
    this._drawerObserver = null;
    this.clearOpenTimer();
    this.clearCycleTimer();
    this._listeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });
    this._listeners = [];
  }

  on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this._listeners.push({ target, type, handler, options });
  }

  get input() {
    return this.closest('form')?.querySelector('input[type="search"]');
  }

  get firstText() {
    return this.getAttribute('data-first-text')?.trim() || '';
  }

  get lastText() {
    return this.getAttribute('data-last-text')?.trim() || '';
  }

  get startDelay() {
    const delay = parseFloat(this.getAttribute('data-delay'));
    return Number.isFinite(delay) ? delay * 1000 : 0;
  }

  get reducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  bindDrawer() {
    const drawer = this.closest('search-drawer');
    if (!drawer) return;

    if (drawer.hasAttribute('active')) this.onDrawerOpen();

    this._drawerObserver = new MutationObserver(() => {
      if (drawer.hasAttribute('active')) {
        this.onDrawerOpen();
      } else {
        this.onDrawerClose();
      }
    });

    this._drawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['active'],
    });
  }

  clearOpenTimer() {
    if (this._openTimer) {
      clearTimeout(this._openTimer);
      this._openTimer = null;
    }
  }

  onDrawerOpen() {
    this.clearOpenTimer();
    this.isDismissed = false;
    this.show();

    const drawer = this.closest('search-drawer');
    const inner = drawer?.querySelector('[data-drawer-inner]');
    let started = false;

    const start = () => {
      if (started) return;
      started = true;
      this.scheduleInit();
    };

    if (inner) {
      inner.addEventListener('transitionend', start, { once: true });
    }

    this._openTimer = setTimeout(start, inner ? 650 : 50);
  }

  onDrawerClose() {
    this.isDismissed = false;
    this.clearOpenTimer();
    this.clearCycleTimer();
    this.stopAnimations();
    this.initialized = false;
    this.textContent = '';
    this.style.width = '0';
    this.cursor?.classList.remove('blink');
    this.show();
  }

  scheduleInit() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.init());
    });
  }

  clearCycleTimer() {
    if (this._cycleTimer) {
      clearTimeout(this._cycleTimer);
      this._cycleTimer = null;
    }
  }

  stopAnimations() {
    this.getAnimations?.().forEach((animation) => animation.cancel());
  }

  hide() {
    const typed = this.closest('.typed');
    if (typed) {
      typed.setAttribute('hidden', '');
      return;
    }

    this.parentElement?.classList.add('is-hidden');
  }

  show() {
    const typed = this.closest('.typed');
    if (typed) {
      typed.removeAttribute('hidden');
      return;
    }

    this.parentElement?.classList.remove('is-hidden');
  }

  onTypedPointerDown(event) {
    event.preventDefault();
    this.input?.focus();
    this.onPointerDown();
  }

  onPointerDown() {
    if (this.input?.value.trim()) return;
    this.dismissTyped();
  }

  onFocus() {
    if (this.isDismissed || this.input?.value.trim()) return;
    this.clearCycleTimer();
    this.stopAnimations();
    this.cursor?.classList.remove('blink');
  }

  onBlur() {
    if (this.input?.value.trim()) return;
    this.restoreTyped();
  }

  onInput() {
    if (this.input?.value.trim()) {
      this.dismissTyped();
    }
  }

  onReset() {
    requestAnimationFrame(() => {
      if (!this.input?.value.trim()) {
        this.restoreTyped();
      }
    });
  }

  dismissTyped() {
    if (this.isDismissed) return;

    this.isDismissed = true;
    this.clearCycleTimer();
    this.stopAnimations();
    this.hide();
  }

  restoreTyped() {
    if (!this.isDismissed || this.input?.value.trim()) return;

    this.isDismissed = false;
    this.initialized = false;
    this.clearCycleTimer();
    this.stopAnimations();
    this.textContent = '';
    this.style.width = '0';
    this.cursor?.classList.remove('blink');
    this.show();
    this.scheduleInit();
  }

  displayText(text) {
    if (!text) return;

    this.insertCursor();
    this.textContent = text;
    this.style.width = 'auto';
    const width = this.scrollWidth;
    this.style.width = width ? `${width}px` : 'auto';
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  animateWidth(width) {
    return new Promise((resolve) => {
      const animation = this.animate(
        { width: `${width}px` },
        {
          duration: width === 0 ? 250 : 1000,
          fill: 'forwards',
          easing: 'ease',
        }
      );
      animation.addEventListener('finish', resolve, { once: true });
      animation.addEventListener('cancel', resolve, { once: true });
    });
  }

  async init() {
    if (this.isDismissed || (!this.firstText && !this.lastText)) return;

    if (this.input?.value.trim()) {
      this.dismissTyped();
      return;
    }

    this.insertCursor();
    this.show();
    this.initialized = true;

    if (this.reducedMotion) {
      this.displayText(this.lastText || this.firstText);
      return;
    }

    await this.start(this.firstText, this.startDelay);
    if (this.isDismissed) return;

    this._cycleTimer = setTimeout(async () => {
      if (this.isDismissed) return;
      await this.reset();
      if (this.isDismissed) return;
      await this.start(this.lastText, 0);
    }, 600);
  }

  async start(text, delay) {
    if (!text || this.isDismissed) return;

    if (delay) await this.wait(delay);
    if (this.isDismissed) return;

    this.textContent = text;
    this.style.width = 'auto';
    const targetWidth = this.scrollWidth;
    this.style.width = '0px';

    if (!targetWidth) {
      this.style.removeProperty('width');
      this.cursor?.classList.add('blink');
      return;
    }

    await this.animateWidth(targetWidth);
    if (this.isDismissed) return;

    this.cursor?.classList.add('blink');
  }

  async reset() {
    if (this.isDismissed) return;

    this.cursor?.classList.remove('blink');

    if (!this.scrollWidth) {
      this.style.width = '0px';
      return;
    }

    await this.animateWidth(0);
  }

  insertCursor() {
    if (this.cursor) return;
    this.cursor = document.createElement('span');
    this.cursor.className = 'typed-cursor';
    this.cursor.setAttribute('aria-hidden', 'true');
    this.cursor.textContent = '|';
    this.parentElement?.insertBefore(this.cursor, this.nextSibling);
  }
}

if (!customElements.get('search-typed')) {
  customElements.define('search-typed', SearchTyped);
}
